<template>
  <g-dialog
    v-model="dialogValue"
    :title="dialogTitle"
    max-width="500"
  >
    <div v-if="currentState === 'select_type'">
      <p class="text-body-1 grey--text text--darken-1 mb-4">
        Select type of contract to scan
      </p>

      <v-radio-group
        v-model="contractType"
        class="mt-2"
      >
        <v-radio
          label="Sales"
          value="sales"
          color="primary"
        />
        <v-radio
          label="Delivery"
          value="delivery"
          color="primary"
        />
      </v-radio-group>

      <div class="d-flex justify-end mt-4">
        <v-btn
          color="primary"
          :disabled="!contractType"
          @click="goToFileUpload"
        >
          Next
          <v-icon right>
            mdi-arrow-right
          </v-icon>
        </v-btn>
      </div>
    </div>

    <div v-else-if="currentState === 'file_upload'">
      <p class="text-body-1 grey--text text--darken-1 mb-4">
        Upload your {{ contractType }} contract file
      </p>

      <AttachmentUpload
        ref="attachmentUpload"
        v-model="files"
        class="pb-4"
        drop-prompt="Drag and drop your contract here"
        @input="handleFileInput"
      />

      <div class="d-flex justify-space-between mt-4">
        <v-btn
          text
          @click="goToSelectType"
        >
          <v-icon left>
            mdi-arrow-left
          </v-icon>
          Back
        </v-btn>

        <v-btn
          v-if="files.length > 0"
          color="primary"
          @click="startUpload"
        >
          <v-icon left>
            mdi-cog
          </v-icon>
          Process
        </v-btn>
      </div>
    </div>

    <div
      v-else-if="currentState === 'uploading'"
      class="text-center py-4"
    >
      <p class="text-body-2 grey--text text--darken-1 mb-4">
        Please wait a moment while we upload your {{ files.length === 1 ? 'file' : 'files' }}
      </p>

      <div class="d-flex align-center justify-center mb-2">
        <v-icon
          class="mr-2"
          color="grey"
        >
          mdi-file-document-outline
        </v-icon>
        <span class="text-body-2">{{ selectedFileName }}</span>
        <span class="text-caption grey--text ml-2">{{ formattedFileSize }}</span>
      </div>

      <v-progress-linear
        :value="uploadProgress"
        color="primary"
        height="8"
        rounded
        class="mb-4"
      />

      <p class="text-caption grey--text">
        {{ uploadProgress }}% uploaded
      </p>
    </div>

    <div
      v-else-if="currentState === 'upload_error'"
      class="text-center py-4"
    >
      <v-icon
        color="error"
        size="48"
        class="mb-4"
      >
        mdi-alert-circle
      </v-icon>

      <p class="text-body-1 error--text mb-2">
        Upload failed
      </p>

      <p class="text-body-2 grey--text mb-4">
        {{ errorMessage }}
      </p>

      <v-btn
        color="primary"
        @click="retryUpload"
      >
        Try Again
      </v-btn>
    </div>

    <div
      v-else-if="currentState === 'processing'"
      class="text-center py-8"
    >
      <v-icon
        v-if="showSuccessCheckmark"
        color="success"
        size="64"
        class="mb-4"
      >
        mdi-check-circle
      </v-icon>
      <v-progress-circular
        v-else
        indeterminate
        color="primary"
        size="64"
        width="4"
        class="mb-4"
      />

      <p class="text-body-1 grey--text text--darken-1">
        {{ processingMessage }}
      </p>
    </div>

    <div
      v-else-if="currentState === 'process_error'"
      class="text-center py-4"
    >
      <v-icon
        color="error"
        size="48"
        class="mb-4"
      >
        mdi-alert-circle
      </v-icon>

      <p class="text-body-1 error--text mb-2">
        Processing failed
      </p>

      <p class="text-body-2 grey--text mb-4">
        {{ errorMessage }}
      </p>

      <v-btn
        color="primary"
        @click="retryUpload"
      >
        Try Again
      </v-btn>
    </div>
  </g-dialog>
</template>

<script>
import { mapMutations, mapActions, mapState } from 'vuex';
import GDialog from '@/components/generic/GDialog.vue';
import AttachmentUpload from '@/components/farm_profile/sales/AttachmentUpload.vue';
import { postFormRequest } from '@/helpers/request';

const STATES = {
  SELECT_TYPE: 'select_type',
  FILE_UPLOAD: 'file_upload',
  UPLOADING: 'uploading',
  UPLOAD_ERROR: 'upload_error',
  PROCESSING: 'processing',
  PROCESS_ERROR: 'process_error',
};

export default {
  name: 'SinoaContractScanner',

  components: {
    GDialog,
    AttachmentUpload,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentState: STATES.SELECT_TYPE,
      contractType: null,
      files: [],
      selectedFile: null,
      selectedFileName: '',
      selectedFileSize: 0,
      uploadProgress: 0,
      errorMessage: '',
      processedData: null,
      processingMessage: 'Please wait a moment while we process your files',
      showSuccessCheckmark: false,
      acceptedFileTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/bmp', 'image/gif'],
      maxFiles: 5,
    };
  },

  computed: {
    ...mapState('contractScanner', ['filteredContractData']),

    dialogValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
        if (!val) {
          this.resetScanner();
        }
      },
    },

    dialogTitle() {
      switch (this.currentState) {
        case STATES.SELECT_TYPE:
        case STATES.FILE_UPLOAD:
          return 'Scan contract';
        case STATES.UPLOADING:
        case STATES.UPLOAD_ERROR:
        case STATES.PROCESSING:
        case STATES.PROCESS_ERROR:
          return 'File uploading';
        default:
          return 'Scan contract';
      }
    },

    formattedFileSize() {
      if (!this.selectedFileSize) return '';
      const kb = this.selectedFileSize / 1024;
      if (kb < 1024) {
        return `${kb.toFixed(0)}KB`;
      }
      return `${(kb / 1024).toFixed(1)}MB`;
    },
  },

  methods: {
    ...mapMutations('chatbot', ['setGrainFoxAssistantDialogOpen']),
    ...mapActions('chatbot', ['handleContractScanResponse']),
    ...mapMutations('contractScanner', ['setFilteredContractData', 'setScannedAttachment', 'setScannedAttachments', 'setInteractionId']),

    goToSelectType() {
      this.currentState = STATES.SELECT_TYPE;
      this.files = [];
      this.selectedFile = null;
    },

    goToFileUpload() {
      if (this.contractType) {
        this.currentState = STATES.FILE_UPLOAD;
      }
    },

    triggerFileInput() {
      if (this.$refs.attachmentUpload) {
        this.$refs.attachmentUpload.triggerFileInput();
      }
    },

    handleFileInput(fileList) {
      if (!fileList || fileList.length === 0) return;

      if (fileList.length > this.maxFiles) {
        this.errorMessage = `Maximum ${this.maxFiles} files allowed. Please remove some files.`;
        this.currentState = STATES.UPLOAD_ERROR;
        return;
      }

      const invalidFiles = fileList.filter((fileObj) => {
        const file = fileObj.file || fileObj;
        return !this.acceptedFileTypes.includes(file.type);
      });

      if (invalidFiles.length > 0) {
        this.errorMessage = 'Some files have incorrect format. Please upload only PDF or image files.';
        this.files = fileList.filter((fileObj) => {
          const file = fileObj.file || fileObj;
          return this.acceptedFileTypes.includes(file.type);
        });
        if (this.files.length === 0) {
          this.currentState = STATES.UPLOAD_ERROR;
        }
        return;
      }

      this.errorMessage = '';
    },

    getFileName(fileObj) {
      const file = fileObj.file || fileObj;
      return file.name || 'Unknown file';
    },

    getFileIcon(fileObj) {
      const file = fileObj.file || fileObj;
      const type = file.type || '';

      if (type.includes('pdf')) {
        return 'mdi-file-pdf-box';
      }
      if (type.includes('image')) {
        return 'mdi-file-image';
      }
      return 'mdi-file-document-outline';
    },

    formatFileSize(fileObj) {
      const file = fileObj.file || fileObj;
      const size = file.size || 0;

      if (size === 0) return '';

      const kb = size / 1024;
      if (kb < 1024) {
        return `${kb.toFixed(0)} KB`;
      }
      return `${(kb / 1024).toFixed(1)} MB`;
    },

    removeFile(index) {
      this.files.splice(index, 1);
    },

    async startUpload() {
      if (this.files.length === 0) return;

      if (this.files.length === 1) {
        const file = this.files[0].file || this.files[0];
        this.selectedFileName = file.name;
        this.selectedFileSize = file.size;
      } else {
        this.selectedFileName = `${this.files.length} files`;
        this.selectedFileSize = this.files.reduce((sum, f) => sum + (f.file?.size || f.size || 0), 0);
      }

      this.currentState = STATES.UPLOADING;
      this.uploadProgress = 0;

      try {
        const progressInterval = setInterval(() => {
          if (this.uploadProgress < 90) {
            this.uploadProgress += 10;
          }
        }, 200);

        const formData = new FormData();

        this.files.forEach((fileObj) => {
          const file = fileObj.file || fileObj;
          formData.append('files', file);
        });
        formData.append('contract_type', this.contractType);

        const response = await postFormRequest('/chatbot/process-contract/', formData);

        clearInterval(progressInterval);
        this.uploadProgress = 100;

        await this.delay(300);
        await this.startProcessing(response);
      } catch (error) {
        this.errorMessage = error.message || 'Failed to upload contracts. Please try again.';
        this.currentState = STATES.UPLOAD_ERROR;
      }
    },

    async startProcessing(response) {
      this.currentState = STATES.PROCESSING;
      this.processingMessage = 'Please wait a moment while we process your file';
      this.showSuccessCheckmark = false;

      try {
        await this.delay(1500);

        if (response && response.success) {
          this.processedData = response;

          if (response.interaction_id) {
            this.setInteractionId(response.interaction_id);
          }

          await this.handleContractScanResponse(response);

          if (response.extracted_data) {
            this.setFilteredContractData(response.extracted_data);
          }

          if (response.attachments && response.attachments.length > 0) {
            this.setScannedAttachment(response.attachments[0]);
          }

          const scannedFiles = this.files.map((fileObj, index) => ({
            id: fileObj.id || index + 1,
            file: fileObj.file || fileObj,
          }));
          this.setScannedAttachments(scannedFiles);

          this.showSuccessCheckmark = true;
          this.processingMessage = 'Contract processed successfully!';
          await this.delay(1000);

          this.showSuccessCheckmark = false;
          this.processingMessage = 'Redirecting...';
          await this.delay(800);

          this.performRedirect();
        } else {
          this.errorMessage = response?.error || 'Failed to process contract. Please try again.';
          this.currentState = STATES.PROCESS_ERROR;
        }
      } catch (error) {
        this.errorMessage = error.message || 'Failed to process contract. Please try again.';
        this.currentState = STATES.PROCESS_ERROR;
      }
    },

    performRedirect() {
      const routeName = this.contractType === 'sales'
        ? 'FarmProfileSalesContractNew'
        : 'FarmProfileSalesDeliveryNew';

      this.dialogValue = false;
      this.setGrainFoxAssistantDialogOpen(false);

      this.$router.push({
        name: routeName,
        params: {
          isContractScanned: true,
          scannedData: this.processedData?.extracted_data || {},
        },
        query: {
          is_from_sinoa: 'true',
        },
      });
    },

    retryUpload() {
      this.currentState = STATES.FILE_UPLOAD;
      this.files = [];
      this.selectedFile = null;
      this.selectedFileName = '';
      this.selectedFileSize = 0;
      this.uploadProgress = 0;
      this.errorMessage = '';
    },

    delay(ms) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    },

    resetScanner() {
      this.currentState = STATES.SELECT_TYPE;
      this.contractType = null;
      this.files = [];
      this.selectedFile = null;
      this.selectedFileName = '';
      this.selectedFileSize = 0;
      this.uploadProgress = 0;
      this.errorMessage = '';
      this.processedData = null;
      this.processingMessage = 'Please wait a moment while we process your file';
    },
  },
};
</script>

<style lang="scss" scoped>
.v-radio-group {
  .v-radio {
    margin-bottom: 8px;
  }
}

.file-list {
  .file-preview-list {
    background: #f5f5f5;
    border-radius: 8px;
    max-height: 200px;
    overflow-y: auto;
  }

  .file-item {
    border-bottom: 1px solid #e0e0e0;

    &:last-child {
      border-bottom: none;
    }
  }
}
</style>
